import React, { useEffect, useState } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import FileInput, { FileDetails } from "@amzn/meridian/file-input";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Select, { SelectOption } from "@amzn/meridian/select";
import { v4 as uuidv4 } from "uuid";
import { uploadFileToS3 } from "./utils";

interface FileUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete: (uploadedFiles: any[]) => void;
    title?: string;
    updateFunction: (newFiles: any[]) => Promise<void>;
    fileTypes?: { value: string; label: string }[];
    maxFiles?: number;
    acceptedFileTypes?: string[];
    modalWidth?: string;
    propertyId: string;
    refetch: any;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
    isOpen,
    onClose,
    onUploadComplete,
    title = "Upload Files",
    updateFunction,
    fileTypes = [{ value: "default", label: "Default" }],
    maxFiles = Infinity,
    acceptedFileTypes = [],
    modalWidth = "600px",
    propertyId,
    refetch,
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const [isUploading, setIsUploading] = useState(false);
    const [fileType, setFileType] = useState<string>("");

    const onFileAttached = (acceptedFiles: File[]) => {
        setFiles(prevFiles => {
            const newFiles = [...prevFiles, ...acceptedFiles];

            return newFiles.slice(0, maxFiles);
        });
    };

    const onClickRemoveFile = (file: File) => {
        setFiles(files.filter(f => f.name !== file.name));
        const newProgress = { ...uploadProgress };
        delete newProgress[file.name];
        setUploadProgress(newProgress);
    };

    const handleUpload = async (propertyId: string) => {
        setIsUploading(true);
        const uploadedFiles = [];
        const newImages = [];

        for (const file of files) {
            try {
                const s3Key = `APPRAISER-AI/IMAGES/PROPERTY/${propertyId}/${uuidv4()}`;
                const result = await uploadFileToS3(file, s3Key, progress => {
                    setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
                });
                uploadedFiles.push(result);
                newImages.push({
                    canopyS3Key: s3Key,
                    fileName: file.name,
                    type: fileType,
                });
            } catch (error) {
                console.error(`Error uploading file ${file.name}:`, error);
            }
        }

        if (newImages.length > 0) {
            await updateFunction(newImages);
        }

        setIsUploading(false);
        onUploadComplete(uploadedFiles);
        onClose();
    };

    const closeModal = () => {
        setFiles([]);
        setUploadProgress({});
        setFileType("");
        onClose();
    };

    useEffect(() => {
        setFiles([]);
        setUploadProgress({});
        setFileType("");
    }, [isOpen]);

    return (
        <Modal
            open={isOpen}
            onClose={closeModal}
            closeLabel="Close"
            scrollContainer="viewport"
            width={modalWidth}
            title={title}
        >
            <Column spacing="400" width="100%">
                {fileTypes.length > 1 && (
                    <Select
                        label="Type"
                        value={fileType}
                        onChange={setFileType}
                        placeholder="Select file type..."
                        width={300}
                    >
                        {fileTypes.map(type => (
                            <SelectOption key={type.value} value={type.value} label={type.label} />
                        ))}
                    </Select>
                )}
                <Text>Upload files</Text>
                <FileInput
                    type="multiple"
                    onFileAttached={onFileAttached}
                    accept={acceptedFileTypes.join(",")}
                >
                    {files.map(file => (
                        <FileDetails
                            key={file.name}
                            file={file}
                            onClickRemoveFile={() => onClickRemoveFile(file)}
                            uploadPercentage={uploadProgress[file.name] || 0}
                            uploadComplete={uploadProgress[file.name] === 100}
                            removeFileButtonLabel={`Delete ${file.name}`}
                        />
                    ))}
                </FileInput>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit" spacing={"300"}>
                    <Button type="secondary" onClick={closeModal} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            handleUpload(propertyId).then(() => {
                                refetch();
                            });
                        }}
                        disabled={
                            files.length === 0 || isUploading || (fileTypes.length > 1 && !fileType)
                        }
                    >
                        {isUploading ? "Uploading..." : "Upload"}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
